import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  HostBinding,
  OnDestroy,
} from '@angular/core';
import { SafeResourceUrlService } from '../../services/safe-resource-url.service';
import { FileTypeService } from '../../services/file-type.service';
import { FileUtilsService } from '../../services/file-utils.service';

const MAP_EXT_TO_ASSET = {
  default: 'assets/img/icons/file-ext/file-default.svg',
  csv: 'assets/img/icons/file-ext/file-csv.svg',
  pdf: 'assets/img/icons/file-ext/file-pdf.svg',
  key: 'assets/img/icons/file-ext/file-slider.svg',
  fig: 'assets/img/icons/file-ext/file-figma.svg',
  'txt|rtf|text': 'assets/img/icons/file-ext/file-text.svg',
  'ppt|pptx': 'assets/img/icons/file-ext/file-slider.svg',
  'doc|docx': 'assets/img/icons/file-ext/file-text.svg',
  'xls|xlsx': 'assets/img/icons/file-ext/file-csv.svg',
  'jpg|png|tiff|bmp|psd|psb|webmp': 'assets/img/icons/file-ext/file-img.svg',
  'eps|ai|svg': 'assets/img/icons/file-ext/file-vector.svg',
  'mov|avi|mp4|gif': 'assets/img/icons/file-ext/file-video.svg',
  'mp3|wav|ogg': 'assets/img/icons/file-ext/file-audio.svg',
  'obj|skp': 'assets/img/icons/file-ext/file-3d.svg',
  'zip|rar': 'assets/img/icons/file-ext/file-zip.svg',
  'xml|json|html|js': 'assets/img/icons/file-ext/file-code.svg',
};
const MAP_EXTENSIONS = Object.keys(MAP_EXT_TO_ASSET).reduce((acc, key) => {
  key.split('|').forEach((ext) => (acc[ext] = MAP_EXT_TO_ASSET[key]));
  return acc;
}, {});

@Component({
  selector: 'selector-newupload-files-preview-thumpnail-component',
  templateUrl: './newupload-files-preview-thumpnail.component.html',
  styleUrl: './newupload-files-preview-thumpnail.component.scss',
  standalone: false,
})
export class NewUploadFilesPreviewThumpnailComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('mtmVideoPlayer', { static: false }) mtmVideoPlayer: ElementRef;
  @ViewChild('previewImage', { static: false }) previewImage: ElementRef;
  @ViewChild('mtmAudioPlayer', { static: true }) mtmAudioPlayer: ElementRef = null;
  @ViewChild('mtmEmbedVideo', { static: true }) mtmEmbedVideo: ElementRef;

  @Input() item: any = null;

  @Input() subTitleFile: boolean = false;

  @Input() fix: boolean = false;
  @Input() fillContainer: boolean = false;
  @Input() mode: string = 'view';

  player: any;
  videoOptions: any;

  mediaType: string = null;
  currentImgSrc = '';

  @HostBinding('class.video-upload') isVideoUpload: boolean = false;
  @HostBinding('class.image-upload') isImageUpload: boolean = false;
  @HostBinding('class.audio-upload') isAudioUpload: boolean = false;

  constructor(
    private readonly safeResourceUrlService: SafeResourceUrlService,
    private readonly fileTypeService: FileTypeService,
    private readonly fileUtilsService: FileUtilsService
  ) {}

  ngOnInit() {
    this.mediaType = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.item) {
        this.mediaType = void 0;
        this.disposePlayerIfExist();
        setTimeout(() => {
          this.init();
        }, 0);
      }
    }
  }

  ngOnDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  }

  disposePlayerIfExist() {
    if (this.player) {
      this.player.dispose();
      this.player = void 0;
    }
  }

  setupCurrentImgSrc() {
    // Prioritize mediaType if set
    if (this.mediaType) {
      switch (this.mediaType) {
        case 'VIDEO':
          this.currentImgSrc = MAP_EXTENSIONS['mp4'];
          return;
        case 'AUDIO':
          this.currentImgSrc = MAP_EXTENSIONS['mp3'];
          return;
        case 'IMAGE':
          this.currentImgSrc = MAP_EXTENSIONS['png'];
          return;
        case 'URL':
          // For URL type, fall through to extension-based logic
          break;
      }
    }

    // Fall back to extension-based logic if mediaType is not set or is 'URL'
    const fileName = this.item.name || '';
    const fileExtension = this.fileUtilsService.getFileExtension(fileName);
    this.currentImgSrc = MAP_EXTENSIONS[fileExtension] || MAP_EXTENSIONS['default'];
  }

  private init() {
    this.isAudioUpload = false;
    this.isImageUpload = false;
    this.isVideoUpload = false;
    if (this.item) {
      if (typeof this.item === 'string' || this.item instanceof String) {
        this.mediaType = 'URL';
      } else if (this.fileTypeService.isVideoFile(this.item.file)) {
        this.mediaType = 'VIDEO';
        this.isVideoUpload = true;
        this.initializeVideoJs();
      } else if (this.fileTypeService.isAudioFileByExtension(this.item.fileType)) {
        this.mediaType = 'AUDIO';
        this.isAudioUpload = true;
        setTimeout(() => {
          this.mtmAudioPlayer.nativeElement.src = this.item.localPlayPath;
          this.mtmAudioPlayer.nativeElement.load();
        }, 2500);
      } else if (
        (this.item.file && this.fileTypeService.isImageFile(this.item.file)) ||
        (!this.item.file && this.fileTypeService.isImageFileByExtension(this.item.fileType))
      ) {
        this.mediaType = 'IMAGE';
        this.isImageUpload = true;
        const fileExtension = this.fileUtilsService.getFileExtension(this.item.name);
        // Preview image if format can be viewed on browser
        if (!/tiff?|psb|psd/.test(fileExtension)) {
          this.setImageUrl(this.item.localPlayPath);
          this.currentImgSrc = this.item.localPlayPath;
          return;
        }
      } else {
        // this.mediaType = 'IMAGE';
        // this.setImageUrl('assets/img/icons/file.svg');
      }

      this.setupCurrentImgSrc();
    } else {
      // this.mediaType = 'IMAGE';
      // this.setImageUrl('assets/img/icons/file.svg');
    }
  }

  private initializeVideoJs() {
    setTimeout(() => {
      if (this.mtmVideoPlayer) {
        this.player = videojs(
          this.mtmVideoPlayer.nativeElement,
          {
            autoplay: false,
            fluid: true,
            preload: 'auto',
          },
          () => {
            this.setVideoSource();
          }
        );
      }
    }, 0);
  }

  private setVideoSource() {
    const source = this.safeResourceUrlService.toSafeUrl(this.item.localPlayPath) as any;
    const type = this.getVideoType();
    if (source && type) {
      this.player.src([{ src: source.changingThisBreaksApplicationSecurity, type: type }]);
    }
  }

  private getVideoType() {
    let fileType: string;
    if (
      (this.item.publicLink && this.isFileExtension(this.item.publicLink, 'mp4')) ||
      (this.item.name && this.isFileExtension(this.item.name, 'mp4'))
    ) {
      fileType = 'video/mp4';
    } else if (
      (this.item.publicLink && this.isFileExtension(this.item.publicLink, 'mkv')) ||
      (this.item.name && this.isFileExtension(this.item.name, 'mkv'))
    ) {
      fileType = 'video/mp4';
    } else if (
      (this.item.publicLink && this.isFileExtension(this.item.publicLink, 'mov')) ||
      (this.item.name && this.isFileExtension(this.item.name, 'mov'))
    ) {
      fileType = 'video/mp4';
    } else if (
      (this.item.publicLink && this.isFileExtension(this.item.publicLink, 'webm')) ||
      (this.item.name && this.isFileExtension(this.item.name, 'webm'))
    ) {
      fileType = 'video/webm';
    } else if (
      (this.item.publicLink && this.isFileExtension(this.item.publicLink, 'flv')) ||
      (this.item.name && this.isFileExtension(this.item.name, 'flv'))
    ) {
      fileType = 'video/x-flv';
    }
    return fileType;
  }

  private setImageUrl(url) {
    setTimeout(() => {
      this.previewImage.nativeElement.src = url;
    }, 500);
  }

  private isFileExtension(filename, extension) {
    return this.fileUtilsService.isFileExtension(filename, extension);
  }
}
